import { useEffect, useState } from "react";
import axios from "axios";
import { Check, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AGES = ["18–24", "25–34", "35–44", "45–54", "55+"];
const HEARD = ["Instagram", "TikTok", "A friend", "My spa or salon", "Search", "Press", "Other"];
const BIZ = ["Med spa", "Salon", "Brows & lashes", "Nails", "Wellness / massage", "Other"];
const EMPTY = { role: "customer", name: "", email: "", phone: "", city: "San Diego", age_range: "", heard_from: "", business_name: "", business_type: "" };

function Field({ id, label, children }) {
  return <div className="field"><label htmlFor={id}>{label}</label>{children}</div>;
}

function Select({ id, value, onChange, options, placeholder }) {
  return (
    <select id={id} data-testid={`wl-${id}`} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export default function WaitlistForm({ onJoined }) {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: typeof v === "string" ? v : v.target.value }));

  useEffect(() => {
    const h = (e) => { setF((s) => ({ ...s, role: e.detail })); };
    window.addEventListener("marova:role", h);
    return () => window.removeEventListener("marova:role", h);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!f.name.trim()) errs.name = "Please add your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) errs.email = "That email doesn't look right";
    if (f.role === "business" && !f.business_name.trim()) errs.business_name = "Add your business name";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, f);
      setDone(data);
      onJoined?.(data);
    } catch (err) {
      setErrors({ form: err?.response?.data?.detail?.[0]?.msg || "Something went wrong — please try again" });
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="wl-success" className="card" style={{ padding: 48, textAlign: "left" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--mint)", display: "flex", alignItems: "center", justifyContent: "center" }}><Check color="#1F5C3A" /></div>
        <h3 className="serif" style={{ fontSize: 48, fontWeight: 500, margin: "24px 0 0", lineHeight: 1.05 }}>{done.already_joined ? "You're already on the list" : `You're in, ${done.name.split(" ")[0]}.`}</h3>
        <p style={{ fontSize: 16, color: "var(--ink-60)", lineHeight: 1.65, marginTop: 14 }}>
          You're <b data-testid="wl-position" style={{ color: "var(--burgundy)" }}>#{done.position}</b> on the {done.role === "business" ? "business" : "San Diego"} waitlist. We'll email you the moment Marova opens in your city — and early members get their first evening planned on us.
        </p>
      </div>
    );
  }

  const isBiz = f.role === "business";
  return (
    <form onSubmit={submit} noValidate data-testid="waitlist-form" className="card" style={{ padding: 40, display: "flex", flexDirection: "column", gap: 22 }}>
      <div className="seg" data-testid="wl-role" style={{ alignSelf: "flex-start" }}>
        <button type="button" data-testid="wl-role-customer" aria-pressed={!isBiz} onClick={() => set("role")("customer")}>For customers</button>
        <button type="button" data-testid="wl-role-business" aria-pressed={isBiz} onClick={() => set("role")("business")}>For businesses</button>
      </div>
      {isBiz && (
        <div className="wl-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Field id="business_name" label="Business name"><input id="business_name" data-testid="wl-business_name" value={f.business_name} onChange={set("business_name")} aria-invalid={!!errors.business_name} placeholder="Aura Med Spa" /></Field>
          <Field id="business_type" label="Type"><Select id="business_type" value={f.business_type} onChange={set("business_type")} options={BIZ} placeholder="Select" /></Field>
        </div>
      )}
      <div className="wl-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field id="name" label={isBiz ? "Contact name" : "Full name"}><input id="name" data-testid="wl-name" value={f.name} onChange={set("name")} aria-invalid={!!errors.name} placeholder="Sophie Reyes" autoComplete="name" /></Field>
        <Field id="email" label="Email"><input id="email" type="email" data-testid="wl-email" value={f.email} onChange={set("email")} aria-invalid={!!errors.email} placeholder="sophie@email.com" autoComplete="email" /></Field>
      </div>
      <div className="wl-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field id="phone" label="Phone (optional)"><input id="phone" type="tel" data-testid="wl-phone" value={f.phone} onChange={set("phone")} placeholder="+1 (619) 555-0142" autoComplete="tel" /></Field>
        <Field id="city" label="City"><input id="city" data-testid="wl-city" value={f.city} onChange={set("city")} autoComplete="address-level2" /></Field>
      </div>
      <div className="wl-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {!isBiz && <Field id="age_range" label="Age range (optional)"><Select id="age_range" value={f.age_range} onChange={set("age_range")} options={AGES} placeholder="Select" /></Field>}
        <Field id="heard_from" label="Where did you hear about us?"><Select id="heard_from" value={f.heard_from} onChange={set("heard_from")} options={HEARD} placeholder="Select" /></Field>
      </div>
      {(errors.name || errors.email || errors.business_name || errors.form) && (
        <div data-testid="wl-error" style={{ fontSize: 13, color: "var(--burgundy)", fontWeight: 600 }}>{errors.form || errors.name || errors.email || errors.business_name}</div>
      )}
      <button type="submit" data-testid="wl-submit" className="btn btn-primary" disabled={busy} style={{ alignSelf: "flex-start", padding: "16px 28px", fontSize: 14, marginTop: 6 }}>
        {busy ? "Joining…" : isBiz ? "Request early access" : "Join the waitlist"} <ArrowRight size={14} />
      </button>
      <p style={{ fontSize: 12, color: "var(--ink-40)", margin: 0 }}>No spam. One email when we open in your city, one when your invite is ready.</p>
    </form>
  );
}
